"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SOURCE_TYPE_ICON } from "@/components/assistant-ui/sourceTypeMeta";
import { useIsMdUp } from "@/hooks/useIsMdUp";
import { useProjectLaunchContextCatalog } from "@/hooks/useProjectLaunchContextCatalog";
import { LAUNCH_CONTEXT_LIMIT_PER_CATEGORY, type LaunchContextCategory } from "@/lib/launch/launchContextTypes";
import type { MentionItem, MentionReference } from "@/lib/mentions/types";
import { cn } from "@/lib/utils";
export interface ProjectHomeLaunchContextPickerProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selected: MentionReference[];
  onSelectedChange: (selection: MentionReference[]) => void;
  /** Layers button trigger; wrapped in Popover on desktop. */
  children: ReactNode;
}
const CATEGORY_LABELS: Record<LaunchContextCategory, string> = {
  document: "Documents",
  insight: "Insights",
  experiment: "Experiments"
};
const EMPTY_MESSAGES: Record<LaunchContextCategory, string> = {
  document: "No documents in this project yet.",
  insight: "No validated insights yet.",
  experiment: "No experiments to reference yet."
};
function filterItems(items: MentionItem[], query: string): MentionItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(item => {
    const haystack = `${item.label} ${item.description ?? ""}`.toLowerCase();
    return haystack.includes(q);
  });
}
function countByCategory(selected: MentionReference[]): Record<LaunchContextCategory, number> {
  const result: Record<LaunchContextCategory, number> = {
    document: 0,
    insight: 0,
    experiment: 0
  };
  for (const mention of selected) {
    if (mention.type === "document" || mention.type === "insight" || mention.type === "experiment") {
      result[mention.type] += 1;
    }
  }
  return result;
}
function LaunchContextPickerBody(t0) {
  const $ = _c(53);
  const {
    projectId,
    selected,
    onSelectedChange,
    onDone,
    variant
  } = t0;
  const {
    documentItems,
    insightItems,
    experimentItems,
    loading,
    error
  } = useProjectLaunchContextCatalog(projectId);
  const [tab, setTab] = useState("document");
  const [query, setQuery] = useState("");
  let t1;
  if ($[0] !== documentItems || $[1] !== experimentItems || $[2] !== insightItems) {
    t1 = {
      document: documentItems,
      insight: insightItems,
      experiment: experimentItems
    };
    $[0] = documentItems;
    $[1] = experimentItems;
    $[2] = insightItems;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const itemsByCategory = t1;
  let t2;
  if ($[4] !== selected) {
    t2 = new Set(selected.map(_temp));
    $[4] = selected;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const selectedKeys = t2;
  let t3;
  if ($[6] !== selected) {
    t3 = countByCategory(selected);
    $[6] = selected;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const counts = t3;
  const activeItems = itemsByCategory[tab];
  let t4;
  if ($[8] !== activeItems || $[9] !== query) {
    t4 = filterItems(activeItems, query);
    $[8] = activeItems;
    $[9] = query;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const filtered = t4;
  let t5;
  if ($[11] !== counts || $[12] !== onSelectedChange || $[13] !== selected || $[14] !== selectedKeys) {
    t5 = item => {
      if (item.type !== "document" && item.type !== "insight" && item.type !== "experiment") {
        return;
      }
      const key = `${item.type}:${item.id}`;
      if (selectedKeys.has(key)) {
        onSelectedChange(selected.filter(m_0 => `${m_0.type}:${m_0.id}` !== key));
        return;
      }
      if (counts[item.type] >= LAUNCH_CONTEXT_LIMIT_PER_CATEGORY) {
        return;
      }
      onSelectedChange([...selected, {
        type: item.type,
        id: item.id,
        label: item.label
      }]);
    };
    $[11] = counts;
    $[12] = onSelectedChange;
    $[13] = selected;
    $[14] = selectedKeys;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleToggle = t5;
  const t6 = variant === "inline" && "rounded-xl border border-border/60 bg-muted/20 p-2";
  let t7;
  if ($[16] !== t6) {
    t7 = cn("flex flex-col gap-2", t6);
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== onDone || $[19] !== variant) {
    t8 = variant === "inline" || variant === "popover" ? <div className="flex items-center justify-between gap-2 px-0.5"><p className="text-muted-foreground text-xs">Add project context to your first message</p>{onDone ? <button type="button" onClick={onDone} className="text-muted-foreground hover:text-foreground text-xs font-medium">Done</button> : null}</div> : null;
    $[18] = onDone;
    $[19] = variant;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = Object.keys(CATEGORY_LABELS);
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== counts || $[23] !== tab) {
    t10 = <div className="flex flex-wrap gap-1">{(t9 as LaunchContextCategory[]).map(category => <button key={category} type="button" onClick={() => {
        setTab(category);
        setQuery("");
      }} className={cn("rounded-full px-2.5 py-1 text-xs font-medium transition-colors", tab === category ? "bg-background text-foreground shadow-sm ring-1 ring-border/60" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground")}>{CATEGORY_LABELS[category]}{counts[category] > 0 ? <span className="text-muted-foreground ml-1">· {counts[category]}</span> : null}</button>)}</div>;
    $[22] = counts;
    $[23] = tab;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Search className="pointer-events-none absolute left-2.5 top-2.5 size-3.5 text-muted-foreground" />;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => setQuery(e.target.value);
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = CATEGORY_LABELS[tab];
  let t14;
  if ($[27] !== t13) {
    t14 = t13.toLowerCase();
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = `Search ${t14}…`;
  let t16;
  if ($[29] !== query || $[30] !== t15) {
    t16 = <div className="relative">{t11}<Input value={query} onChange={t12} placeholder={t15} className="h-9 border-border/60 bg-background pl-8 text-sm" /></div>;
    $[29] = query;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== counts || $[33] !== error || $[34] !== filtered || $[35] !== handleToggle || $[36] !== loading || $[37] !== query || $[38] !== selectedKeys || $[39] !== tab || $[40] !== variant) {
    t17 = loading ? <div className="flex items-center justify-center py-8"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div> : error ? <p className="text-destructive px-1 text-sm">{error}</p> : <ul className={cn("overflow-y-auto overscroll-contain", variant === "sheet" ? "max-h-[min(20rem,50vh)]" : "max-h-44")} role="listbox" aria-label={CATEGORY_LABELS[tab]}>{filtered.length === 0 ? <li className="px-2 py-4 text-center text-sm text-muted-foreground">{query.trim() ? "No matches" : EMPTY_MESSAGES[tab]}</li> : filtered.map(item_0 => {
        const key_0 = `${item_0.type}:${item_0.id}`;
        const checked = selectedKeys.has(key_0);
        const category_0 = item_0.type as LaunchContextCategory;
        const atLimit = !checked && counts[category_0] >= LAUNCH_CONTEXT_LIMIT_PER_CATEGORY;
        const Icon = item_0.type === "document" || item_0.type === "insight" || item_0.type === "experiment" ? SOURCE_TYPE_ICON[item_0.type] : SOURCE_TYPE_ICON.document;
        return <li key={key_0}><button type="button" role="option" aria-selected={checked} disabled={atLimit} onClick={() => handleToggle(item_0)} className={cn("flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors", checked ? "bg-primary/10" : "hover:bg-muted/50", atLimit && "cursor-not-allowed opacity-50")}><Icon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden={true} /><span className="min-w-0 flex-1"><span className="block truncate text-sm leading-snug">{item_0.label}</span>{item_0.description ? <span className="text-muted-foreground block truncate text-xs">{item_0.description}</span> : null}</span>{checked ? <Check className="text-primary mt-0.5 size-4 shrink-0" aria-hidden={true} /> : null}</button></li>;
      })}</ul>;
    $[32] = counts;
    $[33] = error;
    $[34] = filtered;
    $[35] = handleToggle;
    $[36] = loading;
    $[37] = query;
    $[38] = selectedKeys;
    $[39] = tab;
    $[40] = variant;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p className="text-muted-foreground px-0.5 text-[11px]">Selections appear as chips above. Up to {LAUNCH_CONTEXT_LIMIT_PER_CATEGORY} per category.</p>;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== onDone || $[44] !== variant) {
    t19 = variant === "sheet" && onDone ? <Button type="button" size="sm" className="w-full" onClick={onDone}>Done</Button> : null;
    $[43] = onDone;
    $[44] = variant;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t10 || $[47] !== t16 || $[48] !== t17 || $[49] !== t19 || $[50] !== t7 || $[51] !== t8) {
    t20 = <div className={t7}>{t8}{t10}{t16}{t17}{t18}{t19}</div>;
    $[46] = t10;
    $[47] = t16;
    $[48] = t17;
    $[49] = t19;
    $[50] = t7;
    $[51] = t8;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
}

/**
 * Context picker for project-home launch: popover anchored to the Layers button (desktop)
 * or bottom sheet (mobile). Toggles apply instantly as chips — no confirm step.
 */
function _temp(m) {
  return `${m.type}:${m.id}`;
}
export function ProjectHomeLaunchContextPicker(t0) {
  const $ = _c(35);
  const {
    projectId,
    open,
    onOpenChange,
    selected,
    onSelectedChange,
    children
  } = t0;
  const isMdUp = useIsMdUp();
  let t1;
  let t2;
  if ($[0] !== isMdUp || $[1] !== onOpenChange || $[2] !== open) {
    t1 = () => {
      if (!open || isMdUp) {
        return;
      }
      const onKeyDown = event => {
        if (event.key === "Escape") {
          onOpenChange(false);
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t2 = [isMdUp, onOpenChange, open];
    $[0] = isMdUp;
    $[1] = onOpenChange;
    $[2] = open;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (isMdUp) {
    let t3;
    if ($[5] !== children) {
      t3 = <PopoverTrigger asChild={true}>{children}</PopoverTrigger>;
      $[5] = children;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== onOpenChange || $[8] !== onSelectedChange || $[9] !== open || $[10] !== projectId || $[11] !== selected) {
      t4 = open ? <LaunchContextPickerBody projectId={projectId} selected={selected} onSelectedChange={onSelectedChange} onDone={() => onOpenChange(false)} variant="popover" /> : null;
      $[7] = onOpenChange;
      $[8] = onSelectedChange;
      $[9] = open;
      $[10] = projectId;
      $[11] = selected;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t4) {
      t5 = <PopoverContent align="start" side="top" sideOffset={8} className="w-[min(100vw-2rem,24rem)] p-2">{t4}</PopoverContent>;
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== onOpenChange || $[16] !== open || $[17] !== t3 || $[18] !== t5) {
      t6 = <Popover open={open} onOpenChange={onOpenChange}>{t3}{t5}</Popover>;
      $[15] = onOpenChange;
      $[16] = open;
      $[17] = t3;
      $[18] = t5;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    return t6;
  }
  let t3;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SheetHeader className="space-y-1 p-0 text-left"><SheetTitle>Add context</SheetTitle><SheetDescription>Selections appear as chips in your message.</SheetDescription></SheetHeader>;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== onOpenChange) {
    t4 = () => onOpenChange(false);
    $[21] = onOpenChange;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== onSelectedChange || $[24] !== projectId || $[25] !== selected || $[26] !== t4) {
    t5 = <SheetContent side="bottom" className="flex max-h-[90vh] flex-col gap-3 overflow-hidden p-4">{t3}<LaunchContextPickerBody projectId={projectId} selected={selected} onSelectedChange={onSelectedChange} onDone={t4} variant="sheet" /></SheetContent>;
    $[23] = onSelectedChange;
    $[24] = projectId;
    $[25] = selected;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== onOpenChange || $[29] !== open || $[30] !== t5) {
    t6 = <Sheet open={open} onOpenChange={onOpenChange}>{t5}</Sheet>;
    $[28] = onOpenChange;
    $[29] = open;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== children || $[33] !== t6) {
    t7 = <>{children}{t6}</>;
    $[32] = children;
    $[33] = t6;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  return t7;
}